// ==========================================
// CONFIGURAÇÃO DO SENTRY - FRONTEND
// ==========================================
// Monitoramento de erros em produção
// Documentação: https://docs.sentry.io/platforms/javascript/react/

import * as Sentry from '@sentry/react';

/**
 * Inicializa o Sentry para monitoramento de erros no frontend
 */
export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  const environment = import.meta.env.VITE_IS_PRODUCTION === 'true' ? 'production' : 'development';
  const isProduction = environment === 'production';

  // Só inicializa se houver DSN configurado
  if (!dsn) {
    console.log('⚠️ Sentry DSN não configurado - monitoramento de erros desabilitado');
    return null;
  }

  try {
    Sentry.init({
      dsn,
      environment,
      
      // Performance Monitoring
      tracesSampleRate: isProduction ? 0.2 : 1.0, // 20% em prod, 100% em dev
      
      // Integrations - BrowserTracing agora está incluído no @sentry/react
      integrations: [
        Sentry.browserTracingIntegration({
          tracePropagationTargets: [
            window.location.origin,
            import.meta.env.VITE_API_URL || 'http://localhost:3001',
          ],
        }),
      ],

      // Filtrar dados sensíveis
      beforeSend(event, hint) {
        // Remover dados sensíveis do evento
        if (event.request) {
          // Remover headers sensíveis
          const sensitiveHeaders = ['authorization', 'cookie', 'x-api-key'];
          sensitiveHeaders.forEach(header => {
            if (event.request.headers && event.request.headers[header]) {
              event.request.headers[header] = '[FILTERED]';
            }
          });

          // Remover dados sensíveis do body
          if (event.request.data) {
            const sensitiveFields = ['password', 'senha', 'token', 'secret', 'api_key'];
            try {
              const data = typeof event.request.data === 'string' 
                ? JSON.parse(event.request.data) 
                : event.request.data;
              
              sensitiveFields.forEach(field => {
                if (data[field]) data[field] = '[FILTERED]';
              });
              
              event.request.data = JSON.stringify(data);
            } catch (e) {
              // Se não for JSON, mantém como está
            }
          }
        }

        return event;
      },

      // Ignorar erros específicos
      ignoreErrors: [
        // Erros de rede comuns
        'Network request failed',
        'Failed to fetch',
        'NetworkError',
        'ECONNREFUSED',
        'ENOTFOUND',
        // Erros de autenticação esperados
        'Token expirado',
        'Não autorizado',
        // Erros de React específicos
        'ResizeObserver loop limit exceeded',
        'ResizeObserver loop completed with undelivered notifications',
      ],

      // Tags padrão
      initialScope: {
        tags: {
          service: 'erp-cobranca-frontend',
          version: import.meta.env.VITE_APP_VERSION || '1.0.0',
        },
      },

      // Configurações de release
      release: `frontend@${import.meta.env.VITE_APP_VERSION || '1.0.0'}`,
    });

    console.log(`✅ Sentry inicializado (${environment})`);
    return Sentry;

  } catch (error) {
    console.error('❌ Erro ao inicializar Sentry:', error.message);
    return null;
  }
}

/**
 * Captura um erro manualmente
 * @param {Error} error - Erro a ser capturado
 * @param {Object} context - Contexto adicional
 */
export function captureError(error, context = {}) {
  if (!import.meta.env.VITE_SENTRY_DSN) return;

  Sentry.withScope(scope => {
    // Adicionar contexto extra
    if (context.user) {
      scope.setUser(context.user);
    }
    if (context.tags) {
      Object.entries(context.tags).forEach(([key, value]) => {
        scope.setTag(key, value);
      });
    }
    if (context.extra) {
      Object.entries(context.extra).forEach(([key, value]) => {
        scope.setExtra(key, value);
      });
    }
    if (context.level) {
      scope.setLevel(context.level);
    }

    Sentry.captureException(error);
  });
}

/**
 * Captura uma mensagem/log manualmente
 * @param {string} message - Mensagem a ser enviada
 * @param {string} level - Nível (info, warning, error)
 * @param {Object} context - Contexto adicional
 */
export function captureMessage(message, level = 'info', context = {}) {
  if (!import.meta.env.VITE_SENTRY_DSN) return;

  Sentry.withScope(scope => {
    scope.setLevel(level);
    
    if (context.tags) {
      Object.entries(context.tags).forEach(([key, value]) => {
        scope.setTag(key, value);
      });
    }
    if (context.extra) {
      Object.entries(context.extra).forEach(([key, value]) => {
        scope.setExtra(key, value);
      });
    }

    Sentry.captureMessage(message);
  });
}

/**
 * Define o usuário atual para rastreamento
 * @param {Object} user - Dados do usuário
 */
export function setUser(user) {
  if (!import.meta.env.VITE_SENTRY_DSN) return;
  
  Sentry.setUser({
    id: user.id,
    email: user.email,
    username: user.nome || user.username,
  });
}

/**
 * Remove o usuário atual (logout)
 */
export function clearUser() {
  if (!import.meta.env.VITE_SENTRY_DSN) return;
  Sentry.setUser(null);
}

/**
 * Adiciona breadcrumb para rastreamento
 * @param {Object} breadcrumb - Dados do breadcrumb
 */
export function addBreadcrumb(breadcrumb) {
  if (!import.meta.env.VITE_SENTRY_DSN) return;
  
  Sentry.addBreadcrumb({
    category: breadcrumb.category || 'custom',
    message: breadcrumb.message,
    level: breadcrumb.level || 'info',
    data: breadcrumb.data,
  });
}

/**
 * Componente de erro boundary para React
 */
export const ErrorBoundary = Sentry.ErrorBoundary;

export { Sentry };
